import { Injectable } from '@angular/core';
import { AppService } from './app.service';
import { AppConfigService } from './app-config.service';
import { requestResponse } from '../models/service-response.model';

export interface ScreenAccessResponseDto {
  allowed: boolean;
  isPublic: boolean;
}

@Injectable({ providedIn: 'root' })
export class UserApiService {
  constructor(
    private readonly appService: AppService,
    private readonly appConfigService: AppConfigService,
  ) {}

  /**
   * Base URL expected to be ".../api/".
   * La URL base del API de negocio debe obtenerse de `servicesAPI` tras el login.
   */
  private get baseUrl(): string {
    const base = this.appConfigService.config.servicesAPI || '';
    return base.endsWith('/') ? base : `${base}/`;
  }

  private get userUrl(): string {
    return `${this.baseUrl}user/`;
  }

  async logOut(): Promise<any> {
    const url = `${this.userUrl}log-out`;
    const response = (await this.appService.put(url, null)) as requestResponse;
    return response?.data;
  }

  async logOutMasked(): Promise<any> {
    const url = `${this.userUrl}log-out-masked`;
    const response = (await this.appService.put(url, null)) as requestResponse;
    return response?.data;
  }

  async changeLanguage(key: string): Promise<any> {
    const url = `${this.userUrl}language`;
    const response = (await this.appService.post(url, { key })) as requestResponse;
    return response?.data;
  }

  async resumeSession(): Promise<any> {
    const url = `${this.userUrl}resume-session`;
    const response = (await this.appService.put(url, null)) as requestResponse;
    return response?.data;
  }

  async changePassword(payload: any): Promise<any> {
    const url = `${this.userUrl}change-password`;
    const response = (await this.appService.put(url, payload)) as requestResponse;
    return response?.data;
  }

  async validateScreenAccess(screenKey: string): Promise<ScreenAccessResponseDto> {
    const url = `${this.userUrl}screen/${encodeURIComponent(screenKey)}/access`;
    const response = (await this.appService.get(url)) as requestResponse;
    return (response?.data ?? { allowed: false, isPublic: false }) as ScreenAccessResponseDto;
  }

  async uploadPhoto(file: File): Promise<{ message: string; photo: string | null }> {
    const url = `${this.userUrl}photo`;
    
    // Convertir File a base64
    const base64 = await this.fileToBase64(file);
    
    // put serializa el cuerpo como JWT (AppService)
    const payload = {
      imageBase64: base64,
      fileName: file.name
    };
    
    const response = (await this.appService.put(url, payload)) as requestResponse;
    // El PhotoResponseDto viene en response.data
    const data = response?.data || {};
    return {
      message: data.message || response?.message || '',
      photo: data.photo || null
    };
  }

  /**
   * Convierte un File a base64 (sin el prefijo data:image/...;base64,)
   */
  private fileToBase64(file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remover el prefijo "data:image/...;base64," si existe
        const base64 = result.includes(',') ? result.split(',')[1] : result;
        resolve(base64);
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  async deletePhoto(): Promise<{ message: string; photo: string | null }> {
    const url = `${this.userUrl}photo`;
    const response = (await this.appService.delete(url)) as requestResponse;
    // El PhotoResponseDto viene en response.data
    const data = response?.data || {};
    return {
      message: data.message || response?.message || '',
      photo: data.photo || null
    };
  }
}

